/**
 * Element-selection walk shared by the hover overlay: from the raw node under
 * the cursor, climb to the nearest text-bearing element, then descend through
 * single-child wrappers to the tightest element that still owns the text.
 *
 * Crucially it reads text via {@link elementText}, which falls back to
 * `textContent` when `innerText` is "" — the browser reports an empty
 * `innerText` for nodes it hasn't painted yet (off-screen under
 * `content-visibility`/deferred rendering). Without the fallback the walk skips
 * a real-but-unpainted node (e.g. a CMS-stamped <p> below the fold) and climbs
 * to an unstamped ancestor, which made `resolveCmsProvenance` miss → an
 * intermittent "couldn't locate this string" after a scroll/refresh. (AHD-7396)
 */
export function elementText(n: HTMLElement | null | undefined): string {
  return n?.innerText?.trim() || n?.textContent?.trim() || ""
}

export function pickTextTarget(
  start: HTMLElement | null,
  body: HTMLElement = document.body
): HTMLElement | null {
  let el: HTMLElement | null = start
  while (el && el !== body && !elementText(el)) el = el.parentElement
  if (!el || el === body) return null
  // Inside a CMS region (html / lexical), the whole element carries the stamp
  // but the cursor lands on an inner <p> / text run. Snap the pick + highlight
  // to the stamped region element so both target the whole field, not a
  // paragraph. scalar regions stamp the element itself, so this is a no-op for
  // them. (T2 / AHD-7515)
  const region = el.closest<HTMLElement>("[data-cms-collection]")
  if (region) return region
  let guard = 0
  while (
    el.children.length === 1 &&
    elementText(el.children[0] as HTMLElement) === elementText(el) &&
    guard++ < 6
  )
    el = el.children[0] as HTMLElement
  if (elementText(el).length > 260) return null
  return el
}
